/**
 * AudioPlayer (TASK-329)
 *
 * Reusable audio player for object URLs (blobs fetched through apiClient with
 * auth headers). Renders a hidden native `<audio>` element with a custom
 * play/pause button, seek slider and time display.
 *
 * Duration is taken from the audio metadata (`loadedmetadata`); when the
 * metadata does not provide a finite duration (common for streamed WAV blobs
 * in some browsers) it falls back to the `durationSeconds` prop.
 */
import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

export interface AudioPlayerProps {
  /** Audio source — typically an object URL created from a fetched blob. */
  src: string;
  /** Fallback duration (seconds) when audio metadata is unavailable. */
  durationSeconds?: number | null;
}

/**
 * Format seconds as `м:сс` (e.g. 65 → "1:05"). Invalid values render "0:00".
 */
// eslint-disable-next-line react-refresh/only-export-components
export function formatAudioTime(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

export function AudioPlayer({
  src,
  durationSeconds,
}: AudioPlayerProps): JSX.Element {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [metaDuration, setMetaDuration] = useState<number | null>(null);

  const fallbackDuration =
    durationSeconds != null && Number.isFinite(durationSeconds)
      ? durationSeconds
      : null;
  const duration = metaDuration ?? fallbackDuration;

  // Reset playback state when the source changes.
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setMetaDuration(null);
  }, [src]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setMetaDuration(audio.duration);
      }
    };
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      const result = audio.play();
      // play() returns a promise in browsers; guard for environments where
      // it does not (jsdom mocks). Autoplay rejection resets the state.
      if (result && typeof result.catch === 'function') {
        result.catch(() => setIsPlaying(false));
      }
      setIsPlaying(true);
    }
  };

  const handleSeek = (value: number[]) => {
    const audio = audioRef.current;
    const time = value[0] ?? 0;
    if (audio) {
      audio.currentTime = time;
    }
    setCurrentTime(time);
  };

  return (
    <div data-testid="audio-player" className="flex w-full items-center gap-3">
      <audio ref={audioRef} src={src} preload="metadata" className="hidden" />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-9 w-9 shrink-0"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
      >
        {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </Button>
      <Slider
        value={[currentTime]}
        min={0}
        max={duration && duration > 0 ? duration : 1}
        step={0.1}
        onValueChange={handleSeek}
        disabled={!duration}
        aria-label="Перемотка"
        className="flex-1"
      />
      <span
        data-testid="audio-player-time"
        className="shrink-0 text-xs tabular-nums text-gray-500"
      >
        {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
      </span>
    </div>
  );
}

export default AudioPlayer;
